
import { useState } from 'react';

export default function BookingForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: '2',
    accommodation: '',
    specialRequests: ''
  });

  const [showPayment, setShowPayment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accommodationPrices = {
    'mountain-retreat': 299,
    'forest-sanctuary': 249,
    'lakeside-escape': 349,
    'desert-oasis': 199
  };

  const accommodationNames = {
    'mountain-retreat': 'Mountain Retreat',
    'forest-sanctuary': 'Forest Sanctuary', 
    'lakeside-escape': 'Lakeside Escape',
    'desert-oasis': 'Desert Oasis'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowPayment(true);
  };

  const handlePayDeposit = async () => {
    setIsSubmitting(true);
    
    try {
      const depositAmount = accommodationPrices[formData.accommodation as keyof typeof accommodationPrices] * 0.3; // 30% deposit
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: depositAmount,
          currency: 'usd',
          description: `Booking Deposit - ${accommodationNames[formData.accommodation as keyof typeof accommodationNames]}`,
          metadata: {
            booking_type: 'retreat_deposit',
            accommodation: formData.accommodation,
            check_in: formData.checkIn,
            check_out: formData.checkOut,
            guests: formData.guests,
            customer_name: `${formData.firstName} ${formData.lastName}`,
            customer_email: formData.email,
            customer_phone: formData.phone
          }
        })
      });

      const { url } = await response.json();
      if (url) {
        // Also submit form data to your form endpoint
        const formBody = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
          formBody.append(key, value);
        });

        await fetch('https://readdy.ai/api/form/d42hgmmmj2r470qad2qg', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString()
        });

        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookWithoutPayment = async () => {
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d42hgmmmj2r470qad2qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        alert('Booking request submitted successfully! We will contact you within 24 hours to confirm your reservation and arrange payment.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          checkIn: '',
          checkOut: '',
          guests: '2',
          accommodation: '',
          specialRequests: ''
        });
        setShowPayment(false);
      } else {
        alert('There was an error submitting your booking. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const selectedPrice = formData.accommodation ? accommodationPrices[formData.accommodation as keyof typeof accommodationPrices] : 0;
  const depositAmount = selectedPrice * 0.3;

  if (showPayment && formData.accommodation) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Secure Your Booking</h2>
        
        <div className="bg-emerald-50 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-emerald-800 mb-4">Booking Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-emerald-700">Accommodation:</span>
              <span className="font-semibold text-emerald-800">
                {accommodationNames[formData.accommodation as keyof typeof accommodationNames]}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Check-in:</span>
              <span className="font-semibold text-emerald-800">{formData.checkIn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Check-out:</span>
              <span className="font-semibold text-emerald-800">{formData.checkOut}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Guests:</span>
              <span className="font-semibold text-emerald-800">{formData.guests}</span>
            </div>
            <div className="border-t border-emerald-200 pt-2 mt-4">
              <div className="flex justify-between">
                <span className="text-emerald-700">Nightly Rate:</span>
                <span className="font-semibold text-emerald-800">${selectedPrice}/night</span>
              </div>
              <div className="flex justify-between">
                <span className="text-emerald-700">Deposit (30%):</span>
                <span className="font-bold text-emerald-800 text-lg">${depositAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <button
            onClick={handlePayDeposit}
            disabled={isSubmitting}
            className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? 'Processing...' : `Pay Deposit ($${depositAmount.toFixed(2)})`}
          </button>
          
          <button
            onClick={handleBookWithoutPayment}
            disabled={isSubmitting}
            className="w-full bg-gray-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? 'Submitting...' : 'Book Without Payment (Pay Later)'}
          </button>
          
          <button
            onClick={() => setShowPayment(false)}
            className="w-full bg-white text-gray-600 py-3 px-6 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Form
          </button>
        </div>

        <p className="text-sm text-gray-600 text-center mt-6">
          Secure payment processed by Stripe. The remaining balance will be due upon arrival.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-8">Make a Reservation</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="booking-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="checkIn" className="block text-sm font-medium text-gray-700 mb-2">
              Check-in Date *
            </label>
            <input
              type="date"
              id="checkIn"
              name="checkIn"
              value={formData.checkIn}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="checkOut" className="block text-sm font-medium text-gray-700 mb-2">
              Check-out Date *
            </label>
            <input
              type="date"
              id="checkOut"
              name="checkOut"
              value={formData.checkOut}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="guests" className="block text-sm font-medium text-gray-700 mb-2">
              Number of Guests *
            </label>
            <select
              id="guests"
              name="guests"
              value={formData.guests}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="1">1 Guest</option>
              <option value="2">2 Guests</option>
              <option value="3">3 Guests</option>
              <option value="4">4 Guests</option>
              <option value="5">5 Guests</option>
              <option value="6">6+ Guests</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="accommodation" className="block text-sm font-medium text-gray-700 mb-2">
              Accommodation Type *
            </label>
            <select
              id="accommodation"
              name="accommodation"
              value={formData.accommodation}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Accommodation</option>
              <option value="mountain-retreat">Mountain Retreat - $299/night</option>
              <option value="forest-sanctuary">Forest Sanctuary - $249/night</option>
              <option value="lakeside-escape">Lakeside Escape - $349/night</option>
              <option value="desert-oasis">Desert Oasis - $199/night</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-2">
            Special Requests or Dietary Requirements
          </label>
          <textarea
            id="specialRequests"
            name="specialRequests"
            value={formData.specialRequests}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
            placeholder="Please let us know about any special requests, dietary requirements, or accessibility needs..."
          />
          <p className="text-xs text-gray-500 mt-1">{formData.specialRequests.length}/500 characters</p>
        </div>

        <button
          type="submit"
          className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Continue to Payment
        </button>
      </form>
    </div>
  );
}
